import React, { useState, useEffect, useRef, useMemo } from "react"
import { createPortal } from "react-dom"

import { paginateAll, paginateChunked } from "../services/api/utils"
import { getMissionsLiteIds } from "../services/api/v2/mission"
import { getPmTimesheets } from "../services/api/v2/timesheet"
import { getPmLeaves } from "../services/api/v2/leave"
import { getMyMissionPeopleLite } from "../services/api/v2/missionPerson"
import { toast } from "react-toastify"
import SearchDropDown from "../comp/SearchDropDown"
import { utcToIsoString } from "../utils/dates"
import { useDebouncedEffect, isGtGr, getRefId, isPersonalProject } from "../utils/utils"
import cx from "classnames"
import BarLoader from "react-spinners/BarLoader"
import { makeRefHash } from "../utils/utils"
import Loadable from "../comp/Loadable"
import Badge from "../comp/Badge"

import Tip from "../comp/Tip"
import Button from "../comp/Button"
import Bubble from "../comp/Bubble"

import Tabs from "../comp/Tabs"
import uniqBy from "lodash/uniqBy"

import { startOfDay, subWeeks, addWeeks, startOfWeek, endOfWeek, subMonths } from "date-fns"

import "./Board.scss"

import { getEncryptionKey } from "../utils/key-utils"
import FullScreen from "../comp/FullScreen"

import {
    PiClockDuotone,
    PiCreditCardDuotone,
    PiAirplaneDuotone,
    PiStackDuotone,
    PiAlignLeftDuotone,
    PiRocketDuotone,
    PiFileTextDuotone,
    PiLightningDuotone,
    PiTableDuotone,
    PiHandGrabbingDuotone,
    PiHandCoinsDuotone,
} from "react-icons/pi"

import history, { addParam } from "../comp/History"
import { publishScreenTabs } from "../utils/screenTabs"
import { applyTabOrder } from "../utils/tabOrder"
import { TbReportMoney } from "react-icons/tb"

const SORT_TIP_TAG = "x-board-tab-sort"

const PmTimesheets = Loadable({
    loader: () => import("../comp/PmTimesheets"),
    loading: () => <span />,
})

const VisionBoard = Loadable({
    loader: () => import("../comp/VisionBoard"),
    loading: () => <span />,
})

const Scheduling = Loadable({
    loader: () => import("../comp/Scheduling"),
    loading: () => <span />,
})

const PmLeaves = Loadable({
    loader: () => import("../comp/PmLeaves"),
    loading: () => <span />,
})

const Docs = Loadable({
    loader: () => import("../comp/Docs"),
    loading: () => <span />,
})

const OERAResourceTimeline = Loadable({
    loader: () => import("../comp/OERAResourceTimeline"),
    loading: () => <span />,
})

const CostTracker = Loadable({
    loader: () => import("../comp/CostTracker"),
    loading: () => <span />,
})

const ProjectPlanner = Loadable({
    loader: () => import("../comp/ProjectPlanner"),
    loading: () => <span />,
})
const PmMissions = Loadable({
    loader: () => import("../comp/PmMissions"),
    loading: () => <span />,
})

const ExpenseTracker = Loadable({ loader: () => import("../comp/ExpenseTrackerPm"), loading: () => <span /> })
const BoardBudgetPro = Loadable({ loader: () => import("../comp/airtable/BoardBudgetPro"), loading: () => <span /> })

const Board = ({ app, theMissionsHaveLanded, stateChange }) => {
    const ranLeaves = useRef()
    const ranTimesheets = useRef()

    // The tabs array is dynamic (expenses/OERA tabs appear async once org data
    // resolves), so its length/order can change after mount. Storing a raw index
    // would silently drift to point at a different tab when that happens - track
    // the active tab's id instead and derive the index fresh from the current
    // `tabs` array wherever a numeric index is actually needed.
    const [activeTabId, setActiveTabId] = useState()
    const [hasPmTabs, setHasPmTabs] = useState()
    const [hasOeraFlow, setHasOeraFlow] = useState()

    const [showUpgrade, setShowUpgrade] = useState()

    const [encryptionKey, setEncryptionKey] = useState()
    const [leavesBadge, setLeavesBadge] = useState(0)
    const [timesheetCount, setTimesheetCount] = useState()
    const [checkedPmStatus, setCheckedPmStatus] = useState()
    const [tabs, setTabs] = useState([])
    const tabsRef = useRef()

    useEffect(() => {
        app.block()
    }, [])

    useEffect(() => {
        tabsRef.current = tabs
    }, [tabs])

    // What mAX's navigate tool and context read (utils/screenTabs.js). Only once
    // the PM check is done: before it, most tabs are missing for everybody.
    useEffect(() => {
        if (!checkedPmStatus || !tabs.length) return

        return publishScreenTabs("board", {
            ids: tabs.map((t) => t.id),
            // The tab bar falls back to the first tab the same way (tabIndex below).
            active: tabs.some((t) => t.id === activeTabId) ? activeTabId : tabs[0]?.id,
        })
    }, [tabs, activeTabId, checkedPmStatus])

    const onTabChange = () => {
        if (!tabsRef.current?.length) return

        const loc = new URLSearchParams(window.location.search)

        // history is a global singleton, so this can fire for navigation that has nothing to
        // do with Board (e.g. leaving the page). Only react when a board-tab param is present.
        if (!loc.has("board-tab")) return

        const tabToSetId = loc.get("board-tab")

        const selectedTab = tabsRef.current.find((t) => t.id === tabToSetId) || tabsRef.current[0]

        setActiveTabId(selectedTab?.id)

        if (!selectedTab?.id) return

        if (selectedTab.id === "leaves") {
            setLeavesBadge(0)
        }

        const currentAppUiState = app?.state?.person?.appUiState ?? {}

        if (selectedTab.id !== currentAppUiState.pmTab) {
            app.personUpdate({
                appUiState: {
                    ...currentAppUiState,
                    pmTab: selectedTab.id,
                },
            })
        }
    }

    const changeTab = (newTabId) => {
        const tabb =
            typeof newTabId === "string" ? tabsRef.current.find((f) => f.id === newTabId) : tabsRef.current[newTabId]

        if (!tabb) return

        addParam("board-tab", tabb.id)
    }

    const holder = async () => {
        const myKey = await getEncryptionKey({ app })

        setEncryptionKey(myKey)
    }

    useEffect(() => {
        if (app.state.board && app.state.person?._id) {
            holder()
        }
    }, [app.state.person?._id, app.state.board])

    useEffect(() => {
        lastStateChange.current = null
        ranRef.current = null
    }, [app.state.mission, app.state.org])

    useEffect(() => {
        const unlisten = history.listen(onTabChange)
        return () => {
            unlisten()
        }
    }, [tabs])

    const restoredTabRef = useRef(false)

    useEffect(() => {
        if (!checkedPmStatus || !tabs.length || restoredTabRef.current) return

        const savedTabId = app?.state?.person?.appUiState?.pmTab
        // A link that names a tab this person has (?board-tab=leaves, mAX's
        // navigate) opens that tab; the saved one is only for arriving without one.
        const urlTabId = new URLSearchParams(window.location.search).get("board-tab")

        if (urlTabId && tabsRef.current.some((f) => f.id === urlTabId)) {
            restoredTabRef.current = true
            onTabChange()
            app.unBlock()
            return
        }

        if (!savedTabId) {
            restoredTabRef.current = true

            // Honor a deep link (e.g. ?board-tab=scheduling) if one was given, otherwise
            // there's nothing saved yet, leave it on the default vision-board tab.
            if (new URLSearchParams(window.location.search).has("board-tab")) {
                onTabChange()
            }
            app.unBlock()
            return
        }

        const savedIndex = tabsRef.current.findIndex((f) => f.id === savedTabId)

        if (savedIndex === -1) {
            // The saved tab isn't in the list yet (more PM tabs may still be loading in) - try again once tabs change
            return
        }

        restoredTabRef.current = true
        changeTab(savedTabId)
        app.unBlock()
    }, [checkedPmStatus, tabs.length])

    const lastStateChange = useRef()
    const ranRef = useRef()

    const initData = async () => {
        const rangeMissions = await paginateAll(
            getMissionsLiteIds({
                participating: true,
                endDate: `gte ${utcToIsoString(subMonths(new Date(), 2))}`,
            }),
        )

        const rangeMissionIds = rangeMissions.map((m) => m._id)

        const pmRes = await paginateChunked({
            fn: (params) => paginateAll(getMyMissionPeopleLite(params)),
            params: {
                project: rangeMissionIds || [],
                isProjectManager: true,
                permission: ["manager", "admin"],
            },
            chunkKey: "project",
            chunkSize: 80,
        })
        const adminRes = await paginateChunked({
            fn: (params) => paginateAll(getMyMissionPeopleLite(params)),
            params: {
                project: rangeMissionIds || [],
                isProjectManager: false,
                permission: ["admin"],
            },
            chunkKey: "project",
            chunkSize: 80,
        })

        // Running only personal projects doesn't open the PM tabs: those live on the Launchpad
        const hasPm = [...(pmRes || []), ...(adminRes || [])].some((m) => !isPersonalProject(m))

        if (hasPm) {
            setHasPmTabs(true)
        }

        setCheckedPmStatus(true)
    }

    useEffect(() => {
        if (theMissionsHaveLanded) {
            initData()
        }
    }, [app.state.missions.length, theMissionsHaveLanded])

    useEffect(() => {
        const gtOrg = app.state.orgs.find((g) => isGtGr(g, "prod"))

        if (!gtOrg) {
            return
        }

        const myProgram = gtOrg.programs?.find((p) => p?.managers?.includes(app.state.person._id))

        if (myProgram) {
            setHasOeraFlow(true)
        }
    }, [app.state.orgs?.length])

    const doLeavesBadge = async () => {
        const gl = await getLeavesBadge({ app })

        setLeavesBadge(gl || 0)
    }

    const doTimesheetsBadge = async () => {
        const weekStart = startOfWeek(subWeeks(new Date(), 2), { weekStartsOn: 1 })
        const weekEnd = endOfWeek(addWeeks(new Date(), 1), { weekStartsOn: 1 })

        const t1 = await getPmTimesheets({
            limit: 1,
            withArchived: false,
            status: ["submitted"],
            count: true,
            select: ["_id"],
            weekStart: `gte ${weekStart?.toISOString()}`,
            weekEnd: `lte ${weekEnd?.toISOString()}`,
        })

        setTimesheetCount(t1.count)
    }
    // Board is the only owner of the right-side admin tools: shown on the Launchpad, hidden on every other tab.
    const shownTabId = (tabs.find((t) => t.id === activeTabId) || tabs[0])?.id

    useEffect(() => {
        if (!tabs?.length) return

        if (shownTabId === "vision-board") {
            app.showAppAdminTools()
        } else {
            app.hideAppAdminTools()
        }
    }, [shownTabId, tabs])

    useEffect(() => {
        return () => {
            app.showAppAdminTools()
        }
    }, [])

    useDebouncedEffect(
        () => {
            if (!app.state.orgs.length) {
                return
            }

            if (hasPmTabs && tabs?.length > 1 && activeTabId !== "leaves" && !ranLeaves.current) {
                ranLeaves.current = true
                doLeavesBadge()
            }
            if (hasPmTabs && tabs?.length > 1 && activeTabId !== "timesheets" && !ranTimesheets.current) {
                ranTimesheets.current = true
                doTimesheetsBadge()
            }
        },
        [tabs, hasPmTabs, activeTabId, app.state.orgs],
        500,
    )

    const memoTabs = useMemo(() => {
        const passAlong = {
            app,
            context: "pm",
            encryptionKey,
            theMissionsHaveLanded,
            stateChange,
        }

        const baseTabs = [
            {
                title: () => (
                    <>
                        <PiRocketDuotone style={{ transform: "translateY(3px)" }} />
                        Launchpad
                    </>
                ),
                id: "vision-board",
                comp: VisionBoard,
                notComponent: true,
                params: {
                    ...passAlong,
                },
            },
        ]

        if (!hasPmTabs) {
            return baseTabs
        }

        const pmTabs = [
            {
                id: "pm-power-view",
                title: "Power view",
                comp: PmMissions,
                notComponent: true,
                params: {
                    ...passAlong,
                },
            },
            {
                id: "budget-pro",
                title: "Budget Radar",
                comp: BoardBudgetPro,
                notComponent: true,
                params: {
                    ...passAlong,
                },
            },
            {
                id: "pm-planner",
                title: "Planner",
                comp: ProjectPlanner,
                notComponent: true,
                params: {
                    ...passAlong,
                },
            },
            {
                id: "cost-tracker",
                title: "Cost tracker",
                comp: CostTrackerWrapper,
                notComponent: true,
                params: {
                    passAlong,
                    context: "multi",
                    app,
                },
            },

            {
                id: "timesheets",
                title: () => (
                    <Badge badgeContent={timesheetCount} color="primary">
                        Timesheets
                    </Badge>
                ),
                comp: PmTimesheets,
                notComponent: true,
                params: {
                    ...passAlong,
                    context: "pm",
                },
            },
        ]

        if (app.state.orgs.some((o) => o.isLicensedActive && !o.expensesDisabled)) {
            pmTabs.push({
                id: "expenses",
                title: "Expenses",
                comp: ExpenseTracker,
                notComponent: true,
                params: {
                    ...passAlong,
                },
            })
        }

        pmTabs.push({
            id: "leaves",
            title: () => (
                <Badge badgeContent={leavesBadge} color="primary">
                    Leaves
                </Badge>
            ),
            comp: PmLeaves,
            notComponent: true,
            params: {
                ...passAlong,
            },
        })

        if (hasOeraFlow) {
            pmTabs.push(
                {
                    id: "gt-request",
                    className: "gt-tab",
                    title: "Request OERA Resources",
                    cb: () => {
                        toast("Upgrade in progress. Please check back shortly")
                    },
                    fn: () => {
                        toast("Upgrade in progress. Please check back shortly")
                    },
                },
                {
                    id: "gt-assign",
                    className: "gt-tab",
                    title: "Assign Approved Resources",
                    notComponent: true,
                    cb: () => {
                        toast("Upgrade in progress. Please check back shortly")
                    },
                    fn: () => {
                        toast("Upgrade in progress. Please check back shortly")
                    },
                },
            )
        }

        pmTabs.push(
            ...[
                {
                    id: "scheduling",
                    title: () => <Tip title="Off-task project scheduling">Scheduling</Tip>,
                    comp: Scheduling,
                    notComponent: true,
                    params: {
                        ...passAlong,
                        context: "pm",
                        app,
                    },
                },
                {
                    id: "docs",
                    title: "Documents",
                    comp: Docs,
                    notComponent: true,
                    params: {
                        ...passAlong,
                    },
                },
            ],
        )

        return [...baseTabs, ...pmTabs]
    }, [leavesBadge, timesheetCount, hasPmTabs, encryptionKey, theMissionsHaveLanded, hasOeraFlow, app, stateChange])

    // The person's own tab order from dragging. A ref so a badge refresh re-applies
    // the latest order even before the profile update lands.
    const tabOrderRef = useRef(app?.state?.person?.appUiState?.pmTabOrder)

    useEffect(() => {
        setTabs(applyTabOrder(memoTabs, tabOrderRef.current))
    }, [memoTabs])

    // Dragged tabs: reorder in place (the open tab stays open, tabIndex follows activeTabId)
    // and keep the order on the person's profile.
    const reorderTabs = (ids) => {
        const current = tabsRef.current || []
        const next = ids.map((id) => current.find((t) => t.id === id)).filter(Boolean)

        if (next.length !== current.length) return

        tabOrderRef.current = ids
        tabsRef.current = next
        setTabs(next)

        app.personUpdate({
            appUiState: {
                ...(app?.state?.person?.appUiState ?? {}),
                pmTabOrder: ids,
            },
        })
    }

    // Derived fresh every render so it can never drift from activeTabId when tabs reshapes.
    const tabIndex = useMemo(() => {
        const idx = tabs.findIndex((t) => t.id === activeTabId)
        return idx === -1 ? 0 : idx
    }, [tabs, activeTabId])

    const MyTabs = Tabs({
        data: tabs,
        activeTabIndex: tabIndex,
        returnArray: true,
        bodyClassName: "x-board-panels",
        onChange: changeTab,
        onReorder: reorderTabs,
    })

    return (
        <>
            <div className="x-pm-board">
                {checkedPmStatus ? (
                    <>
                        {MyTabs[0]}
                        {MyTabs[1]}
                    </>
                ) : (
                    <>
                        <div className="dna-col x-board-loading-bar">
                            <BarLoader color={"#666666"} loading={true} width={110} />
                        </div>
                    </>
                )}

                {/*hasOeraFlow && (isAssignmentMode || isAllocationMode) && (
                    <OERAResourceTimeline
                        app={app}
                        setParentDates={() => {}}
                        fetchHolidays={() => {}}
                        missionChange={stateChange}
                        isAssignmentMode={isAssignmentMode}
                        isAllocationMode={isAllocationMode}
                        onClose={() => {
                            setIsAllocationMode(true)
                        }}
                    />
                )*/}
            </div>
            {/* On body, centred on the screen under the tab bar, same as the Org tabs tip. */}
            {checkedPmStatus &&
                tabs.length > 1 &&
                !app.state.createMission &&
                createPortal(
                    <div
                        style={{
                            position: "fixed",
                            top: 62,
                            left: "50%",
                            transform: "translateX(-50%)",
                            zIndex: 1, // Board has no layer of its own: stay under time tracking (2) and every full screen
                        }}
                    >
                        <Bubble
                            app={app}
                            tag={SORT_TIP_TAG}
                            x={0}
                            y={0}
                            width={240}
                            position="relative"
                            arrow="top"
                            msg={
                                <>
                                    <p>
                                        <b>Order your tabs</b> <PiHandGrabbingDuotone color="rgb(0, 255, 0)" />
                                    </p>
                                    <p style={{ marginBottom: 120 }}>
                                        Drag any tab to move it in the position you want so the tools you use are always
                                        up front.
                                    </p>
                                </>
                            }
                        />
                    </div>,
                    document.body,
                )}
            {!app.state.createMission && (
                <Bubble
                    app={app}
                    tag="x-pm-space-nav"
                    x={24}
                    y={70}
                    top={0}
                    delay={0}
                    position="fixed"
                    arrow={"top"}
                    msg={
                        <>
                            <p>
                                <b>The PM WORK SPACE</b> <PiStackDuotone color="rgb(0, 255, 0)" />
                            </p>
                            <p>
                                <p>
                                    As a project or program manager, you'll see more tabs and features for managing
                                    budgets, people, timesheets, and more. Otherwise, it's a great visual launchpad for
                                    your projects.
                                </p>
                            </p>
                        </>
                    }
                />
            )}

            <FullScreen
                portal
                style={{ zIndex: 99999 }}
                className="dna-dark-bg"
                open={showUpgrade}
                onClose={() => setShowUpgrade(false)}
            >
                <div className="dna-col" style={{ paddingTop: 50 }}>
                    <h1>🔍 🏺</h1>
                    <h2>Hi {app.state.person.firstName}.</h2>

                    <p className="dna-center" style={{ width: 430, fontSize: 16 }}>
                        The vault has been replaced with an all new global search. Looking for old missions? Use the
                        magnifying glass in the top right corner to find both new projects and older ones that time has
                        forgotten.
                    </p>
                    <div className="dna-center">
                        <Button
                            mint
                            onClick={() => {
                                app.setState({
                                    showSearch: true,
                                })
                                setShowUpgrade(false)
                            }}
                        >
                            Search Now
                        </Button>
                        &nbsp;
                        <Button
                            onClick={() => {
                                setShowUpgrade(false)
                            }}
                        >
                            Back to the Mission
                        </Button>
                    </div>
                </div>
            </FullScreen>
        </>
    )
}

const CostTrackerWrapper = ({ app, passAlong, state }) => {
    const orgs = useMemo(() => {
        return app.state.orgs
    }, [app.state.orgs])

    const getOrgId = localStorage.getItem("x-pm-cost-tracker-org")

    const [orgData, setOrgData] = useState(
        orgs.length === 1 ? orgs[0] : getOrgId ? orgs.find((o) => o._id === getOrgId) : null,
    )

    return (
        <div className="board-c-tracker">
            {orgs.length > 1 && (
                <div className="board-c-tracker-search">
                    <SearchDropDown
                        selectedObj={orgData}
                        data={orgs}
                        className={cx("w-300", {
                            "x-field-mint": orgData,
                            "x-field-orange": !orgData,
                        })}
                        placeHolder="Select an organization..."
                        onSelect={(label, obj) => {
                            if (obj) {
                                localStorage.setItem("x-pm-cost-tracker-org", obj._id)
                                setOrgData(obj)
                            }
                        }}
                        selectOnBlur={false}
                    />

                    {!orgData && <p style={{ marginTop: 20 }}>👆 Please select an organization to begin</p>}
                </div>
            )}
            {orgData && <CostTracker {...passAlong} context="multi" orgData={orgData} />}
        </div>
    )
}

export const getLeavesBadge = async ({ app }) => {
    const st = startOfDay(new Date())

    const orgPeoples = makeRefHash(app.state.orgs.flatMap((o) => o?.people || []))

    try {
        const ls = await paginateAll(
            getPmLeaves({
                limit: 100,
                withArchived: false,
                overlapsDateRangeFrom: utcToIsoString(st),
                overlapsDateRangeTo: utcToIsoString(endOfWeek(addWeeks(st, 3), { weekStartsOn: 1 })),
                status: ["pending_approval"],
                codeId: ["annual-leave"],
                select: ["reviews.by", "person"],
            }),
        )

        const cleaned = uniqBy(ls || [], "person")

        const neededReviews = cleaned?.filter((l) => {
            const orgPerson = orgPeoples[l.person]

            if (!orgPerson) {
                return false
            }

            const orgData = app.state.orgs.find((o) => o._id === orgPerson.org)
            const orgRole = orgPerson?.role ? orgData?.roles?.find((o) => o._id === orgPerson.role) : null

            const isExempt = orgRole ? orgData?.leaveApproveExemptRoles?.find((r) => r === orgRole?._id) : false

            if (isExempt) {
                return false
            }

            return l.person !== getRefId(app.state.person._id) && !l.reviews.find((r) => r.by === app.state.person._id)
        })

        return neededReviews?.length
    } catch (e) {
        return 0
    }
}

export default Board
